import { router } from '@inertiajs/react'
import { Sparkles, ThumbsDown, ThumbsUp, Wand2 } from 'lucide-react'
import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { useRouteParams } from '@/hooks/useRoutes'
import { PromoteClusterDialog } from './PromoteClusterDialog'

export interface ClusterRow {
  id: string
  suggested_title: string | null
  suggested_tags: string[]
  canonical_phrasing: string
  size: number
  promoted_at: string | null
  dismissed_at: string | null
}

interface ClustersListProps {
  clusters: ClusterRow[]
}

export function ClustersList({ clusters }: ClustersListProps) {
  const { withAccount } = useRouteParams()
  const [promoting, setPromoting] = useState<ClusterRow | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  const openPromote = (c: ClusterRow) => { setPromoting(c); setDialogOpen(true) }

  const dismiss = (id: string) =>
    router.post(`/${withAccount().account_id}/shortcuts/clusters/${id}/dismiss`, {}, { preserveScroll: true })

  const runScan = () =>
    router.post(`/${withAccount().account_id}/shortcuts/ai_scan`, {}, { preserveScroll: true })

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="size-4 text-primary" /> AI-discovered patterns
          </CardTitle>
          <CardDescription>
            Groups of similar replies we've spotted in your past conversations. Promote one to turn it into a canned answer — you can tweak the title before saving.
          </CardDescription>
        </div>
        <Button onClick={runScan} variant="outline" size="sm" className="cursor-pointer shrink-0">
          <Wand2 className="size-4 mr-1" /> Run scan
        </Button>
      </CardHeader>
      <CardContent>
        {clusters.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No patterns yet. Run a scan to look for repeated replies you can turn into shortcuts.
          </p>
        ) : (
          <ul className="space-y-2">
            {clusters.map(c => (
              <li key={c.id} className="flex gap-3 items-start bg-muted/30 rounded-lg p-3">
                <div className="bg-primary/20 text-primary text-xs font-semibold rounded px-2 py-1 min-w-[40px] text-center shrink-0">
                  {c.size}
                </div>
                <div className="flex-1 min-w-0">
                  {c.suggested_title && (
                    <div className="text-sm font-medium mb-0.5">{c.suggested_title}</div>
                  )}
                  <div className="text-xs text-muted-foreground font-mono">{c.canonical_phrasing}</div>
                  {c.suggested_tags.length > 0 && (
                    <div className="flex gap-1 mt-1.5">
                      {c.suggested_tags.map(t => (
                        <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" onClick={() => openPromote(c)} className="cursor-pointer">
                    <ThumbsUp className="size-3 mr-1" /> Promote
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => dismiss(c.id)} className="cursor-pointer" title="Dismiss">
                    <ThumbsDown className="size-3" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <PromoteClusterDialog open={dialogOpen} onOpenChange={setDialogOpen} cluster={promoting} />
    </Card>
  )
}
